import { CommonModule } from "@angular/common";
import { Component, inject, Input } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { RestService } from "../../../core/services/rest.service";
import { ToasterService } from "../../../core/services/toaster-service";
import { UtilitiesService } from "../../../core/services/utilities.service";
import { MessageEnum } from "../../../core/utils/message.enum";
import { LoadingComponent } from "../../common/loading/loading.component";

@Component({
  selector: "app-data-display-modal",
  standalone: true,
  imports: [CommonModule, FormsModule, LoadingComponent],
  templateUrl: "./data-display-modal.component.html",
  styleUrl: "./data-display-modal.component.scss",
})
export class DataDisplayModalComponent {
  isLoading: boolean = false;
  fields: any = {};

  @Input()
  mainFields: any = {};

  @Input()
  configs: any = {};

  @Input()
  data: any = {};
  isActive: boolean = false;
  
  isFieldLoading(field: any): boolean {
    return field?.isLoading === true;
  }

  selectedFields: any[] = [];
  activeModal = inject(NgbActiveModal);
  showPassword: { [key: string]: boolean } = {};

  constructor(
    public restService: RestService,
    public toaster: ToasterService,
    private utilitiesServices : UtilitiesService
  ) {}

  ngOnInit() {
    if(!this.configs?.isCustom){
      this.getProvidersList();
    }
  }

  /** Keys that should never appear in the Dynamic Configuration display. */
  private readonly hiddenFieldKeys = new Set(['additional_attributes']);

  getData(){
    if (this.configs?.subTitle) {
      const provider: any = this.data?.meta?.source;
      const section = this.fields.find(
        (section: any) => section.value === provider
      );
      this.selectedFields = (section?.fields || []).filter(
        (f: any) => !this.hiddenFieldKeys.has(f.key)
      );

      this.selectedFields.forEach((field: any) => {
        if (field.type === "password-input") {
          this.showPassword[field.key] = false;
        }
      });
    }

    const providerConfig = this.fields.find((p: any) => p.value === this.data?.meta?.source);
    let fields: any[] = (providerConfig?.fields || [])
      .filter((f: any) => !this.hiddenFieldKeys.has(f.key))
      .map((f: any) => ({ ...f }));
    // let models = providerConfig?.models || [];

    // Apply conditionalFields: build visibleIf for each field listed under any condition value
    const conditionalFieldsConfig: any[] = providerConfig?.conditionalFields || [];
    if (conditionalFieldsConfig.length > 0) {
      const visibleIfMap: Record<string, { key: string; value: any }> = {};

      conditionalFieldsConfig.forEach((conditional: any) => {
        Object.entries(conditional.conditions).forEach(([conditionValue, fieldKeys]) => {
          // Parse string boolean keys to their actual boolean type
          let parsedValue: any = conditionValue;
          if (conditionValue === 'true') parsedValue = true;
          else if (conditionValue === 'false') parsedValue = false;

          (fieldKeys as string[]).forEach((fieldKey: string) => {
            visibleIfMap[fieldKey] = { key: conditional.key, value: parsedValue };
          });
        });
      });

      fields = fields.map((field: any) =>
        visibleIfMap[field.key] ? { ...field, visibleIf: visibleIfMap[field.key] } : field
      );
      this.selectedFields = this.getVisibleFields(fields) || [];
    }

  }


  getVisibleFields(fields: any[]) {
    let result: any = [];
    fields.forEach((field: any) => {
      if (!field.visibleIf || (field.visibleIf?.key && fields.find(f => f.key === field.visibleIf.key)?.value) === field.visibleIf.value) {
        result.push(field);
      }
    });
    return result;
  }

  getProvidersList() {
    this.isLoading = true;
    this.restService.getLLMProviderFields(this.configs?.type).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        this.fields = this.utilitiesServices.sortByLabel(response?.data);
        this.getData();
      },
      error: (error: any) => {
        this.isLoading = false;
        this.toaster.pop({
          type: "error",
          text: MessageEnum.ERROR_GETTING_PROVIDER_FIELDS,
        });
      },
    });
  }

  togglePassword(fieldKey: string) {
    this.showPassword[fieldKey] = !this.showPassword[fieldKey];
  }

  getMaskedValue(value: string): string {
    return "•".repeat(value?.length || 0);
  }

  /** Explicit overrides for keys whose backend-sent name isn't display-ready. */
  private readonly fieldLabelOverrides: Record<string, string> = {
    connectivity_type: 'Connectivity Type',
  };

  /** Returns the display label for a field, applying any explicit overrides. */
  getFieldLabel(field: any): string {
    return this.fieldLabelOverrides[field?.key] ?? field?.name ?? field?.key ?? '';
  }

  /** Resolves a field's value from the data object.
   *  LLM connections store credentials under meta.credentials.*
   *  ALM/DB connections store them directly under meta.* */
  getFieldValue(field: any): any {
    return this.data?.meta?.credentials?.[field.key]
      ?? this.data?.meta?.[field.key]
      ?? null;
  }

  /** Expands the contents of `additional_attributes` into displayable rows. */
  get additionalAttributeEntries(): { label: string; value: string }[] {
    const raw =
      this.data?.meta?.credentials?.additional_attributes ??
      this.data?.meta?.additional_attributes;

    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];

    return Object.entries(raw)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([k, v]) => ({
        label: this.fieldLabelOverrides[k] ?? k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        value: String(v),
      }));
  }
  
  get leftFields() {
    const mid = Math.ceil(this.mainFields.length / 2);
    return this.mainFields.slice(0, mid);
  }

  get rightFields() {
    const mid = Math.ceil(this.mainFields.length / 2);
    return this.mainFields.slice(mid);
  }

  buttonClick(){
    if(this.configs?.isEdit){
      this.activeModal.close({githubLink: this.data?.githubLink});
     //
    } else{
      this.activeModal.dismiss('cancel');
    }
  }
}
